/**
 * 选择层：**底部抽屉（窄）／居中弹窗（宽）**，同一个组件、同一份行为，只有容器形态随断点变。
 *
 * 目前只服务「被投诉方选择器」（`RespondentPicker.tsx`），但刻意写成与业务无关的壳子——
 * 里面装什么由调用方给，这里只负责六件容易做漏的事：
 *
 *   ① **挂在哪**（这是本文件最要紧的一条）
 *      手机端整套 UI 装在一只 390×844 的手机壳里（`base.css` 的 `.phone > .screen`），
 *      桌面预览时它只占屏幕正中一小块。若抽屉用 `position:fixed`，在桌面预览下就会
 *      **盖满整个浏览器窗口**、糊在手机壳外面。所以：
 *        · 触发元素能找到 `.screen` 祖先 → portal 进那只 `.screen`，`position:absolute`；
 *        · 找不到（PC 页 `pc.html`）→ portal 进 `document.body`，`position:fixed`。
 *      顺带解决了另一件事：抽屉不再是表单 DOM 的后代，不会被表单的 `overflow` 裁掉。
 *
 *   ② **层级压过小助手悬浮球**：球是 `z-index:110`（base.css「小助手悬浮球」节），
 *      本层 200，并且**打开时直接把球藏掉**（给 `.screen` 挂 `rp-modal-open`，
 *      规则写在 respondent.css）。只压层级不藏球的话，球会浮在遮罩上——
 *      看着像能点、实际点不到，是**假状态**。
 *      全站结果弹窗 `.modal-mask` 是 300，仍在本层之上，不抢它的位。
 *
 *   ③ **背景锁滚且不跳位**：
 *      · 手机壳里滚的是 `.scroll` 那个容器 → 记下 `scrollTop`，锁成 `overflow:hidden`，
 *        关闭时写回。（`overflow:hidden` 会把 `scrollTop` 清零，所以**必须**显式写回，
 *        否则关掉抽屉人就被甩回表单顶部。）
 *      · PC 页滚的是文档 → `body{position:fixed;top:-y}` 把页面钉住，
 *        关闭时复位并 `scrollTo(0,y)`。不用 `html{overflow:hidden}`：那会让页面
 *        在遮罩后面先跳到顶部，用户一关抽屉就发现自己换了地方。
 *
 *   ④ **三条关闭路径**：点遮罩、点关闭键、按 Esc。少一条都会出现「关不掉」的死角。
 *
 *   ⑤ **焦点**：打开时把焦点收进面板并做 Tab 环，关闭后还给触发行。
 *      `autoFocusField` 由调用方给一个选择器；**触摸设备上不自动聚焦输入框**——
 *      手机上一聚焦就弹软键盘，可视高度被砍掉一半，抽屉当场被顶变形，
 *      而用户此刻真正要做的第一件事往往是「看看上次查的结果」，不是马上打字。
 *      所以触摸端焦点落在面板本身（可读屏、可按 Esc、可 Tab 到搜索框），
 *      鼠标端才直接落进输入框。
 *
 *   ⑥ **底部安全区**：iPhone 底部横条会压住抽屉最后一行，靠 `env(safe-area-inset-bottom)`
 *      垫开（做法与 base.css 的 `.tabbar/.botbar` 一致）。
 *
 * 动效：升起/淡入走 CSS 动画，`prefers-reduced-motion: reduce` 下整个关掉，
 * 直接出现、直接消失，**功能一条不少**（见 respondent.css 末尾那段）。
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** 关闭动画时长，与 respondent.css 的 `rp-sink/rp-dim-out` 同值。改一处必须改另一处。 */
const CLOSE_MS = 190;

/** 用户是否要求减少动效。每次读实时值——系统开关可能中途改。 */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * 锁住背景滚动，返回解锁函数。**解锁必须把滚动位置原样还回去**。
 *
 * `scroller` 非空＝手机壳里的 `.scroll` 容器；为空＝PC 页，滚的是文档本身。
 */
function lockScroll(scroller: HTMLElement | null): () => void {
  if (scroller) {
    const top = scroller.scrollTop;
    const prev = scroller.style.overflow;
    scroller.style.overflow = 'hidden';
    return () => {
      scroller.style.overflow = prev;
      // overflow:hidden 期间 scrollTop 被清零，这里写回原位
      scroller.scrollTop = top;
    };
  }
  const body = document.body;
  const y = window.scrollY;
  const prev = {
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
  };
  body.style.position = 'fixed';
  body.style.top = `-${y}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  return () => {
    body.style.position = prev.position;
    body.style.top = prev.top;
    body.style.left = prev.left;
    body.style.right = prev.right;
    body.style.width = prev.width;
    window.scrollTo(0, y);
  };
}

export interface PickerSheetProps {
  /**
   * 开关。**由调用方持有**，收起动画由本组件自己播完再卸载——
   * 所以「点中一条」「点遮罩」「按关闭」「按 Esc」四条路只要都把它置 false，
   * 收起的样子就完全一致；哪一条自己直接卸载组件，哪一条就会变成"啪一下消失"。
   */
  open: boolean;
  /** 抽屉标题，同时用作 `aria-label` */
  title: string;
  /**
   * 触发元素。用它就近找 `.screen`（手机壳）与 `.scroll`（滚动容器）——
   * 组件自己判断该挂哪儿、该锁谁，调用方不用关心两个入口的差别。
   */
  anchor: HTMLElement | null;
  /** 请求关闭（遮罩／关闭键／Esc 三条路都走这里，调用方据此把 `open` 置 false） */
  onClose: () => void;
  /**
   * 打开后要聚焦的元素选择器（面板内）。**只在非触摸设备上生效**，
   * 触摸设备焦点落在面板上，避免软键盘顶变形抽屉。
   */
  autoFocusField?: string;
  /** 面板正文（自己滚的那块由调用方标 `rp-sheet-scroll`） */
  children: ReactNode;
  /** 常驻底栏（如「名录里没找到？手工填写」）。安全区内边距在这里吃。 */
  footer?: ReactNode;
}

export function PickerSheet({
  open,
  title,
  anchor,
  onClose,
  autoFocusField,
  children,
  footer,
}: PickerSheetProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** 还在 DOM 里没有（关闭动画期间仍为 true） */
  const [mounted, setMounted] = useState(open);
  /** 正在播关闭动画：这一帧还在，只是往下沉 */
  const [closing, setClosing] = useState(false);
  const timer = useRef<number | null>(null);

  /* ---- 挂载点：手机壳内的 .screen，或 PC 页的 body ---- */
  const screen = anchor ? anchor.closest<HTMLElement>('.screen') : null;
  const scroller = anchor ? anchor.closest<HTMLElement>('.scroll') : null;
  const host: HTMLElement | null = screen ?? (typeof document === 'undefined' ? null : document.body);

  /* ---- open → 立刻出现；open=false → 先播完收起动画再卸载 ---- */
  useEffect(() => {
    if (open) {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
      setClosing(false);
      setMounted(true);
      return;
    }
    if (!mounted || timer.current !== null) return;
    // 关掉动效的用户要的是"直接消失"，不是慢一拍的消失
    if (prefersReducedMotion()) {
      setClosing(false);
      setMounted(false);
      return;
    }
    setClosing(true);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setClosing(false);
      setMounted(false);
    }, CLOSE_MS);
  }, [open, mounted]);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  /* ---- 锁滚 ＋ 藏悬浮球：挂载期间有效，卸载时原样恢复 ----
     ★ 标记挂在 `document.body` 上，**不能挂在 `.screen` 上**（2026-08-27 实测踩到）：
       `.screen` 的 className 由 `Shell.tsx` 用 React 管着（打字时会加 `typing`），
       而抽屉一打开就会聚焦搜索框、触发那条 focusin → Shell 重渲染 → React 把
       className 整串写回去，**外部 classList.add 上去的类当场被抹掉**，
       悬浮球又浮回遮罩上。body 不归 React 渲染，挂在它上面才稳。 */
  useEffect(() => {
    if (!mounted) return;
    const unlock = lockScroll(scroller);
    document.body.classList.add('rp-modal-open');
    return () => {
      document.body.classList.remove('rp-modal-open');
      unlock();
    };
  }, [mounted, scroller]);

  /* ---- 焦点：进面板 → 关闭后还给触发行 ---- */
  useEffect(() => {
    if (!mounted) return;
    const prevFocus = document.activeElement as HTMLElement | null;
    const anchorEl = anchor;
    const panel = panelRef.current;
    // 触摸端不自动聚焦输入框（软键盘会把抽屉顶变形），焦点先落在面板上
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const field = !coarse && autoFocusField ? panel?.querySelector<HTMLElement>(autoFocusField) : null;
    (field ?? panel)?.focus({ preventScroll: true });
    return () => {
      // 焦点必须回到表单里，不能留在被移除的节点上（那等于回到 body，
      // 键盘用户一按 Tab 就得从页首重新数起）。
      if (prevFocus && document.contains(prevFocus)) {
        prevFocus.focus({ preventScroll: true });
        return;
      }
      // 触发行此刻可能已被换成「已选卡片」——那就落到卡片上的「重选」，位置是同一处
      anchorEl?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
    };
    // anchor / autoFocusField 只在打开那一刻取值，之后不重跑
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  /* ---- Esc 关闭 ＋ Tab 环（焦点不许溜到背景表单上） ---- */
  useEffect(() => {
    if (!mounted || closing) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'button:not([disabled]),input:not([disabled]),select,textarea,a[href],[tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
      if (items.length === 0) {
        e.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [mounted, closing, onClose]);

  if (!host || !mounted) return null;

  /* `--shell`＝挂在手机壳里（absolute，永远是底部抽屉）；
     `--page`＝挂在 PC 页上（fixed，宽屏居中弹窗、窄下来自动落回底部抽屉）。 */
  const layerCls = [
    'rp-layer',
    screen ? 'rp-layer--shell' : 'rp-layer--page',
    closing ? 'rp-layer--closing' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <div className={layerCls} onMouseDown={onClose} role="presentation">
      <div
        className="rp-panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
        // 面板内按下不冒泡到遮罩，否则在列表上按住拖一下就把抽屉关了
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="rp-sheet-h">
          <h4>{title}</h4>
          <button type="button" className="rp-sheet-x" aria-label="关闭" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.7" strokeLinecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
        {children}
        {footer ? <div className="rp-sheet-f">{footer}</div> : null}
      </div>
    </div>,
    host,
  );
}
